/**
 * User Management API service.
 *
 * Centralizes all user-management-related API calls with consistent error handling
 * and TypeScript types.
 *
 * @module userApi
 */

import { apiDelete, apiGet, apiPut } from '@/lib/apiUtils';

/**
 * User role type
 */
export type UserRole = 'student' | 'instructor' | 'admin';

/**
 * CEFR level type
 */
export type CefrLevel = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';

/**
 * User interface matching backend structure
 */
export interface User {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  level: CefrLevel | null;
  profileImageUrl?: string;
  isActive: boolean;
  isVerified: boolean;
  createdAt: string;
  lastLogin: string;
}

/**
 * User update data
 */
export interface UserUpdateData {
  userType?: UserRole;
  isActive?: boolean;
}

/**
 * Get all users
 *
 * @returns Promise resolving to array of user objects
 * @throws Error if API request fails
 */
export const getUsers = async (): Promise<User[]> => {
  const result = await apiGet<User[]>('/admin/users', {
    errorMessage: 'Failed to fetch users',
    context: 'getUsers',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to fetch users');
  }
  return result;
};

/**
 * Update user role
 *
 * @param userId - User ID to update
 * @param role - New role
 * @returns Promise resolving to updated user object
 * @throws Error if API request fails
 */
export const updateUserRole = async (
  userId: string,
  role: UserRole,
): Promise<User> => {
  const result = await apiPut<User>(
    `/admin/users/${userId}/role`,
    { userType: role },
    {
      errorMessage: 'Failed to update user role',
      context: 'updateUserRole',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to update user role');
  }
  return result;
};

/**
 * Update user status (active/inactive)
 *
 * @param userId - User ID to update
 * @param isActive - New active status
 * @returns Promise resolving to updated user object
 * @throws Error if API request fails
 */
export const updateUserStatus = async (
  userId: string,
  isActive: boolean,
): Promise<User> => {
  const result = await apiPut<User>(
    `/admin/users/${userId}/status`,
    { isActive },
    {
      errorMessage: 'Failed to update user status',
      context: 'updateUserStatus',
      throwOnError: true,
    },
  );
  if (!result) {
    throw new Error('Failed to update user status');
  }
  return result;
};

/**
 * Update user (can update both role and status)
 *
 * @param userId - User ID to update
 * @param updateData - Data to update
 * @returns Promise resolving to updated user object
 * @throws Error if API request fails
 */
export const updateUser = async (
  userId: string,
  updateData: UserUpdateData,
): Promise<User> => {
  const result = await apiPut<User>(`/admin/users/${userId}`, updateData, {
    errorMessage: 'Failed to update user',
    context: 'updateUser',
    throwOnError: true,
  });
  if (!result) {
    throw new Error('Failed to update user');
  }
  return result;
};

/**
 * Delete a user by ID
 *
 * @param userId - User ID to delete
 * @returns Promise resolving on successful deletion
 * @throws Error if API request fails
 */
export const deleteUser = async (userId: string): Promise<void> => {
  await apiDelete(`/admin/users/${userId}`, {
    errorMessage: 'Failed to delete user',
    context: 'deleteUser',
    throwOnError: true,
  });
};
